import React, { useMemo } from 'react';
import TableRow from '@mui/material/TableRow';
import { CustomerTableColumns } from '../../../../configs/customer';
import { Customer } from '../../../../data/entities';
import Status, { ColorTheme } from '../../../../shared/components/Status';
import { CUSTOMER_TIER, PRODUCT_CATEGORY } from '../../../../data/enums';
import CustomTable from '../../../../shared/components/CustomTable';
import { StyledTableCell, CategoryContainer } from '../../../../shared/components/CustomTable/Styled';
import { StyledLink, ContactName, ContactInfo } from './Styled';
import { ProductFilterType, valueToText } from '../../../../configs/filterTypes';
import { CustomerFilterType } from '../../../../configs/filterTypes';
import { StyledNavLink } from '../../../../shared/styled/TableStyles';
import styled from 'styled-components';

const NO_CONTACT = 'Chưa có';
const PRODUCT_ORDERS_PATH = '/product-orders';

interface CustomerRowProps {
    item: Customer;
    tierColorMap?: Record<string, ColorTheme>;
}

const defaultTierColorMap: Record<string, ColorTheme> = {
    [CUSTOMER_TIER.TIER_1]: 'PENDING',
    [CUSTOMER_TIER.TIER_2]: 'WARNING',
    [CUSTOMER_TIER.TIER_3]: 'INFO',
};

const getCategoryColorTheme = (category: string) => {
    switch (category) {
        case PRODUCT_CATEGORY.BARREL:
            return "SUCCESS";
        case PRODUCT_CATEGORY.VESSEL:
            return "WARNING";
        case PRODUCT_CATEGORY.VEHICLE:
            return "INFO";
        default:
            return "INFO";
    }
};

const StyledStatus = styled(Status)`
    flex-shrink: 0;
    flex: 0 1 auto;
    min-width: 0;
`;

export const CustomerRow: React.FC<CustomerRowProps> = React.memo(
    ({ item, tierColorMap = defaultTierColorMap }) => {
        const contactInfo = useMemo(() => {
            // Ensure contact_list is an array before accessing
            const firstContact = Array.isArray(item.contact_list) && item.contact_list.length > 0 
                ? item.contact_list[0]?.contact 
                : null;
            if (!firstContact) return NO_CONTACT;

            return (
                <>
                    <ContactName>{firstContact.name}</ContactName>
                    {firstContact.phone && (
                        <>
                            {' '}
                            - <ContactInfo>{firstContact.phone}</ContactInfo>
                        </>
                    )}
                    {firstContact.email && (
                        <>
                            {' '}
                            - <ContactInfo>{firstContact.email}</ContactInfo>
                        </>
                    )}
                </>
            );
        }, [item.contact_list]);

        const categories = item.category?.split(';').filter(Boolean) || [];

        return useMemo(
            () => (
                <TableRow>
                    <StyledTableCell align="left">
                        <StyledNavLink to={`/customers/${item.id}`}>
                            {item.customer_code ? item.customer_code : item.id}
                        </StyledNavLink>
                    </StyledTableCell>
                    <StyledTableCell align="left">{item.name}</StyledTableCell>
                    <StyledTableCell align="left">
                        <CategoryContainer>
                            {categories.map((category) => (
                                <StyledStatus
                                    key={category}
                                    colorTheme={getCategoryColorTheme(category)}
                                    text={valueToText(ProductFilterType, "category", category)}
                                    width="auto"
                                />
                            ))}
                        </CategoryContainer>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        {item.total_products_purchased || 0}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        <Status
                            text={valueToText(
                                CustomerFilterType,
                                'tier',
                                item.tier
                            )}
                            width="110px"
                            colorTheme={tierColorMap[item.tier]}
                        />
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        <StyledLink
                            to={`${PRODUCT_ORDERS_PATH}?search=${item.customer_code ? item.customer_code : item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item.number_of_orders}
                        </StyledLink>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        <StyledLink
                            to={`${PRODUCT_ORDERS_PATH}?search=${item.customer_code ? item.customer_code : item.id}&status=Open,Processing,BuyingMaterial,InProduction,ImportingToStorage,Delivering`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item.number_of_current_orders}
                        </StyledLink>
                    </StyledTableCell>
                </TableRow>
            ),
            [item, contactInfo, tierColorMap]
        );
    }
);

interface CustomerTableProps {
    customers: Customer[];
    columns?: typeof CustomerTableColumns;
}

const CustomerTable: React.FC<CustomerTableProps> = ({
    customers,
    columns = CustomerTableColumns,
}) => {
    return (
        <CustomTable
            itemList={customers}
            RowElement={CustomerRow}
            TableColumns={columns}
        />
    );
};

export default CustomerTable;
